/**
 * PURE decision core of the impression buffer (hex: no DOM, no RN, no
 * timers, no network, no module state — every function takes its inputs as
 * params and returns a value). Platform shells own the singleton state,
 * timers, leave-signal listeners and transport; THIS module owns every
 * decision they make, so the logic unit-tests without jsdom/RN and cannot
 * drift between platforms:
 *
 *   - web:    `lib/analytics/impression-buffer.ts` (sendBeacon/keepalive,
 *             visibilitychange/pagehide leave signals)
 *   - mobile: `apps/mobile/lib/impression-buffer.ts` (kraahlFetch/Bearer,
 *             AppState->background leave signal)
 *
 * Lives in @kraahl/contract because it is the SINGLE SoT for producer
 * behaviour tied to the wire contract's batching semantics, imported by
 * both platforms — same precedent as `item-card-state` / the presentation
 * helpers (one canonical declaration, never a per-platform fork).
 *
 * Defaults implement the approved O6/O7 knobs:
 *   - O6 throttle: the same listingId is not re-buffered within 60s per tab
 *     /app session. Anti scroll-jitter (snap back-and-forth, grid re-entry),
 *     NOT semantic dedup — the DB stays append-only and a genuinely repeated
 *     exposure >60s later counts again.
 *   - O7 flush triggers: count reaches 20 | 10s idle since the last record
 *     | platform leave signal (web: visibilitychange->hidden / pagehide;
 *     mobile: AppState->background). Leave signals are pure shell concerns
 *     (event wiring); the first two are decisions made here.
 *   - Batch cap: 50 events per beacon (contract MAX) — larger backlogs are
 *     sliced into multiple requests.
 *
 * @see ./track-impressions.ts — wire schema + cap
 */

import {
  MAX_IMPRESSION_EVENTS_PER_BATCH,
  type ImpressionEvent,
} from "./track-impressions";

export const IMPRESSION_THROTTLE_MS = 60_000;
export const IMPRESSION_FLUSH_AT_COUNT = 20;
export const IMPRESSION_IDLE_FLUSH_MS = 10_000;

/**
 * O6 throttle decision: should this listingId be buffered now?
 * `lastBufferedAtMs` is the per-session map of listingId -> last time it
 * was buffered. Missing entry = never buffered = record.
 */
export function shouldBufferImpression(
  lastBufferedAtMs: ReadonlyMap<string, number>,
  listingId: string,
  nowMs: number,
  throttleMs: number = IMPRESSION_THROTTLE_MS,
): boolean {
  const last = lastBufferedAtMs.get(listingId);
  if (last === undefined) return true;
  return nowMs - last >= throttleMs;
}

/**
 * O7 count trigger: flush as soon as the buffer holds `flushAt` events.
 */
export function shouldFlushAtCount(
  bufferedCount: number,
  flushAt: number = IMPRESSION_FLUSH_AT_COUNT,
): boolean {
  return bufferedCount >= flushAt;
}

/**
 * O7 idle trigger (decision form): has the buffer been idle — no new record
 * — long enough to flush? The shells implement this with a reset-on-record
 * timer; this predicate exists so the threshold semantics are pinned by a
 * unit test rather than living only inside setTimeout wiring.
 */
export function shouldIdleFlush(
  lastRecordAtMs: number,
  nowMs: number,
  idleMs: number = IMPRESSION_IDLE_FLUSH_MS,
): boolean {
  return nowMs - lastRecordAtMs >= idleMs;
}

/**
 * Slice a drained backlog into contract-sized batches (≤50 per beacon),
 * preserving order. Empty input -> no batches (never emit an empty beacon —
 * the schema requires min 1 event).
 */
export function sliceIntoBatches(
  events: readonly ImpressionEvent[],
  maxPerBatch: number = MAX_IMPRESSION_EVENTS_PER_BATCH,
): ImpressionEvent[][] {
  if (events.length === 0) return [];
  const batches: ImpressionEvent[][] = [];
  for (let i = 0; i < events.length; i += maxPerBatch) {
    batches.push(events.slice(i, i + maxPerBatch));
  }
  return batches;
}

/**
 * Normalise a client-supplied position into the wire shape: non-negative
 * integers pass through, anything else is dropped (position is optional).
 */
export function normalizeImpressionPosition(position: number | null | undefined): number | undefined {
  if (typeof position !== "number" || !Number.isInteger(position) || position < 0) {
    return undefined;
  }
  return position;
}
